import { Box, Stack, Typography } from '@chronosphereio/chrono-ui';
import { getFormattedDateTimeRange, getFormattedDateTimeString, TimeFormat } from '@chronosphereio/core';
import { isSameDay } from 'date-fns';
import { Close as CloseIcon } from 'mdi-material-ui';
import { memo } from 'react';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { useTimeZone } from '../context';
import { PIN_TOOLTIP_HELP_TEXT, UNPIN_TOOLTIP_HELP_TEXT } from '../PanelTooltip';
import { TOOLTIP_MAX_WIDTH } from '../TimeSeriesTooltip/tooltip-model';

export const TOOLTIP_HEADER_FONT_SIZE = 11;

export interface TooltipHeaderProps {
  /**
   * Start date
   */
  startDate?: Date;
  /**
   * End date
   */
  endDate?: Date;
  /**
   * Controls whether pinning is enabled
   * @default true
   */
  enablePinning?: boolean;
  /**
   * If true, show "Unpin". Otherwise, show "click chart to pin"
   */
  isTooltipPinned?: boolean;
  /**
   * Callback for unpinning a tooltip
   */
  onUnpinClick?: () => void;
}

/**
 * Formats the tooltip header time to ensure sufficient specificity for time ranges
 * (e.g. including the end date when the range crosses day boundaries) while managing
 * screen real estate (e.g. limiting time to minutes when we need to show end dates).
 */
function formatTooltipHeaderTime(
  startDate: Date | undefined,
  endDate: Date | undefined,
  timeFormat: TimeFormat,
  timeZone: string | undefined
): string | undefined {
  if (!startDate) {
    return;
  }

  if (startDate && !endDate) {
    // Only start date, including day of week.
    return getFormattedDateTimeString({ date: startDate.toString(), timeFormat, timeZone, includeDayOfWeek: true });
  } else if (startDate && endDate) {
    // Show start and end date, but do not include day of week for ranges to avoid redundancy.
    return getFormattedDateTimeRange({
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      timeFormat,
      timeZone,
      omitSeconds: !isSameDay(startDate, endDate), // Omit seconds to save space when start and end dates are on different days.
    });
  }
}

export const TooltipHeader = memo(function TooltipHeader({
  startDate,
  endDate,
  isTooltipPinned,
  enablePinning = true,
  onUnpinClick,
}: TooltipHeaderProps) {
  const { timeZone } = useTimeZone();
  const timeFormat = useUserTimeFormat();

  const renderTooltipHeader = () => {
    const formattedTime = formatTooltipHeaderTime(startDate, endDate, timeFormat, timeZone);
    if (!formattedTime) {
      return null;
    }
    return <Typography variant="body2">{formattedTime}</Typography>;
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: TOOLTIP_MAX_WIDTH,
        position: 'sticky',
        top: 0,
        left: 0,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'start',
          alignItems: 'center',
          paddingBottom: 0.5,
        }}
      >
        {renderTooltipHeader()}
        <Stack direction="row" gap={1} sx={{ marginLeft: 'auto' }}>
          {enablePinning && (
            <Stack direction="row" alignItems="center">
              <Typography
                sx={{
                  marginLeft: 2,
                  marginRight: 0.5,
                  fontSize: TOOLTIP_HEADER_FONT_SIZE,
                  verticalAlign: 'middle',
                }}
                color={(theme) => (theme.featureFlags.panwRebrandStubYear ? theme.palette.text.placeholder : undefined)}
              >
                {isTooltipPinned ? UNPIN_TOOLTIP_HELP_TEXT : PIN_TOOLTIP_HELP_TEXT}
              </Typography>
              {isTooltipPinned && (
                <CloseIcon
                  onClick={() => {
                    if (onUnpinClick !== undefined) {
                      onUnpinClick();
                    }
                  }}
                  sx={{
                    fontSize: 16,
                    cursor: 'pointer',
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
});
